import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import Menu from '../Menu/Menu';
import MenuLabel from '../MenuLabel/MenuLabel';
import MenuContent from '../MenuContent/MenuContent';
import MenuItem from '../MenuItem/MenuItem';
import MenuAction from '../MenuAction/MenuAction';
import Icon from '../Icon/Icon';
import css from './RowActionsMenu.module.css';

/**
 * The "⋯" actions menu of a table row (audience lists, productions, emails, settings). The
 * trigger looks like an IconButton; the rows are `RowActionsMenu.Item` (a MenuAction: a button,
 * or a link with `linkProps`, optionally `destructive`), and their clicks don't reach the row.
 * `quiet`: the trigger as the dashboard tables draw it, the grey dots alone until hover.
 */
const RowActionsMenu = props => {
  const {
    className,
    contentPosition,
    contentPlacementOffset,
    triggerClassName,
    contentClassName,
    quiet,
    children,
  } = props;
  const intl = useIntl();

  // MenuContent requires <MenuItem> children with keys, so each Item is wrapped here. Null or
  // false children (from `{cond && <Item />}`) are skipped.
  const items = React.Children.toArray(children)
    .filter(Boolean)
    .map((child, index) => {
      const key = child.props?.itemKey || child.key || `row-actions-${index}`;
      return <MenuItem key={key}>{child}</MenuItem>;
    });

  return (
    <Menu
      className={classNames(css.root, className)}
      useArrow={false}
      contentPosition={contentPosition}
      contentPlacementOffset={contentPlacementOffset}
    >
      <MenuLabel
        className={classNames(css.trigger, { [css.quiet]: quiet }, triggerClassName)}
        ariaLabel={intl.formatMessage({ id: 'RowActionsMenu.label' })}
      >
        <Icon name="more" size={16} />
      </MenuLabel>
      <MenuContent className={classNames(css.content, contentClassName)}>{items}</MenuContent>
    </Menu>
  );
};

RowActionsMenu.defaultProps = {
  className: null,
  // 'left' keeps the menu inside the viewport when the trigger sits in a right-aligned column
  contentPosition: 'left',
  contentPlacementOffset: 0,
  triggerClassName: null,
  contentClassName: null,
  quiet: false,
};

RowActionsMenu.propTypes = {
  className: PropTypes.string,
  contentPosition: PropTypes.oneOf(['left', 'right']),
  contentPlacementOffset: PropTypes.number,
  triggerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  quiet: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

// One row: a MenuAction. `itemKey` gives it a stable key when rows come from a .map().
RowActionsMenu.Item = MenuAction;

export default RowActionsMenu;
